// Gemeinsame Bewegung für Medien-Slider und Kundenbewertungen.
// Als Custom Element startet es von selbst, auch wenn der Theme-Editor die Sektion neu aufbaut.
//
// Attribute am Element:
//   data-motion     manual | autoplay | marquee
//   data-interval   Sekunden zwischen zwei Schritten (autoplay)
//   data-speed      Sekunden pro Element (marquee)
//   data-per-view   Elemente pro Ansicht ab 990 px; ab 750 px max. 3, darunter 1,25
// Kinder: [data-viewport], [data-track] mit den Elementen, optional [data-prev] / [data-next]
if (!customElements.get('aero-carousel')) {
  const GAP = 16;

  class AeroCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-viewport]');
      this.track = this.querySelector('[data-track]');
      if (!this.viewport || !this.track) return;

      this.querySelector('[data-prev]')?.addEventListener('click', () => this.scrollStep(-1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.scrollStep(1));

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!reduceMotion && this.dataset.motion === 'marquee') {
        this.startMarquee();
      } else {
        // Manuell, automatisch oder bei reduzierter Bewegung: Wischen/Blättern ohne Ende
        this.startInfiniteScroll();
        if (!reduceMotion && this.dataset.motion === 'autoplay') this.startAutoplay();
      }
      this.watchVideos();
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      this.observer?.disconnect();
    }

    items() {
      return [...this.track.children].filter((el) => !el.hasAttribute('data-clone'));
    }

    scrollStep(direction) {
      const width = this.items()[0]?.getBoundingClientRect().width || 300;
      this.track.scrollBy({ left: direction * (width + GAP), behavior: 'smooth' });
    }

    startAutoplay() {
      let paused = false;
      this.viewport.addEventListener('mouseenter', () => (paused = true));
      this.viewport.addEventListener('mouseleave', () => (paused = false));
      this.viewport.addEventListener('touchstart', () => (paused = true), { passive: true });
      this.viewport.addEventListener('touchend', () => setTimeout(() => (paused = false), 4000), { passive: true });
      this.timer = setInterval(() => {
        if (paused || document.hidden) return;
        this.scrollStep(1);
      }, (Number(this.dataset.interval) || 5) * 1000);
    }

    // Kopien der Elemente vor und hinter die Reihe setzen. Nach dem Wischen springt die
    // Position unsichtbar um eine Reihenlänge zurück, so kommt nach dem letzten wieder das erste.
    startInfiniteScroll() {
      const originals = this.items();
      if (originals.length < 2) return;
      this.track.querySelectorAll('[data-clone]').forEach((el) => el.remove());
      const makeClone = (item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('data-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('data-shopify-editor-block');
        return clone;
      };
      const first = originals[0];
      originals.forEach((item) => this.track.insertBefore(makeClone(item), first));
      const after = originals.map((item) => this.track.appendChild(makeClone(item)));
      this.track.style.position = 'relative';

      // Länge einer kompletten Reihe inkl. Abstände
      const period = () => after[0].offsetLeft - first.offsetLeft;
      const jump = (left) => {
        const snap = this.track.style.scrollSnapType;
        this.track.style.scrollSnapType = 'none';
        this.track.scrollTo({ left, behavior: 'instant' });
        this.track.style.scrollSnapType = snap;
      };
      jump(first.offsetLeft);

      let settleTimer;
      // Position immer im Bereich [Start - halbe Reihe, Start + halbe Reihe) halten.
      // Ein Sprung um genau eine Reihe zeigt identische Inhalte und ist daher unsichtbar.
      const wrap = () => {
        const start = first.offsetLeft;
        const p = period();
        const left = this.track.scrollLeft;
        if (left < start - p / 2) jump(left + p);
        else if (left >= start + p / 2) jump(left - p);
      };
      this.track.addEventListener('scroll', () => {
        clearTimeout(settleTimer);
        settleTimer = setTimeout(wrap, 150);
      }, { passive: true });
      this.onResize = () => jump(first.offsetLeft);
      window.addEventListener('resize', this.onResize);
    }

    startMarquee() {
      const originals = this.items();
      if (!originals.length) return;
      this.track.querySelectorAll('[data-clone]').forEach((el) => el.remove());
      originals.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('data-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('data-shopify-editor-block');
        this.track.appendChild(clone);
      });

      this.onResize = () => {
        const w = window.innerWidth;
        const desktop = Number(this.dataset.perView) || 3;
        const perView = w >= 990 ? desktop : w >= 750 ? Math.min(desktop, 3) : 1.25;
        const itemWidth = (this.viewport.clientWidth - (Math.ceil(perView) - 1) * GAP) / perView;
        this.style.setProperty('--ac-item-w', `${itemWidth}px`);
        this.style.setProperty('--ac-shift', `${-(itemWidth + GAP) * originals.length}px`);
        this.style.setProperty('--ac-duration', `${(Number(this.dataset.speed) || 5) * originals.length}s`);
      };
      this.onResize();
      window.addEventListener('resize', this.onResize);
      this.classList.add('is-marquee');
    }

    watchVideos() {
      const videos = this.querySelectorAll('video');
      if (!videos.length || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(
        (entries) => entries.forEach((e) => (e.isIntersecting ? e.target.play().catch(() => {}) : e.target.pause())),
        { threshold: 0.4 }
      );
      videos.forEach((v) => this.observer.observe(v));
    }
  }

  customElements.define('aero-carousel', AeroCarousel);
}
